import React, { useState } from 'react';
import axios from 'axios';
import {
    Button,
    Form,
    Modal,
    Spinner,
    Toast,
    ToastContainer,
} from 'react-bootstrap';

import { ReportsData } from '../interface';
import { dataToCsv } from '../csvUtils';
import { globalURLS } from '../../../Global/urls.js';
import { useReportContext } from '../../../Context/index';

import './AdStatsModal.css';

export interface AdStatsSummary {
    totalViews: number;
    uniqueViews: number;
    totalClicks: number;
    uniqueClicks: number;
    totalCtaClicks: number;
    uniqueCtaClicks: number;
}

interface Props {
    show: boolean;
    report: ReportsData | null;
    onHide: () => void;
}

function pickNumber(obj: Record<string, unknown>, keys: string[]): number {
    for (const key of keys) {
        const val = obj[key];
        if (typeof val === 'number' && !Number.isNaN(val)) return val;
        if (val !== undefined && val !== null && val !== '') {
            const n = Number(String(val));
            if (!Number.isNaN(n)) return n;
        }
    }
    return 0;
}

function normalizeAdStats(data: unknown): AdStatsSummary | null {
    if (!data || typeof data !== 'object') return null;
    const r = data as Record<string, unknown>;
    return {
        totalViews: pickNumber(r, [
            'totalViews',
            'total_views',
            'TotalViews',
        ]),
        uniqueViews: pickNumber(r, [
            'uniqueViews',
            'unique_views',
            'UniqueViews',
        ]),
        totalClicks: pickNumber(r, [
            'totalClicks',
            'total_clicks',
            'TotalClicks',
        ]),
        uniqueClicks: pickNumber(r, [
            'uniqueClicks',
            'unique_clicks',
            'UniqueClicks',
        ]),
        totalCtaClicks: pickNumber(r, [
            'totalCtaClicks',
            'total_cta_clicks',
            'totalCTAClicks',
            'TotalCtaClicks',
        ]),
        uniqueCtaClicks: pickNumber(r, [
            'uniqueCtaClicks',
            'unique_cta_clicks',
            'uniqueCTAClicks',
            'UniqueCtaClicks',
        ]),
    };
}

const METRIC_LABELS: { key: keyof AdStatsSummary; label: string }[] = [
    { key: 'totalViews', label: 'Total Views' },
    { key: 'uniqueViews', label: 'Unique Views' },
    { key: 'totalClicks', label: 'Total Clicks/Skips' },
    { key: 'uniqueClicks', label: 'Unique Clicks/Skips' },
    { key: 'totalCtaClicks', label: 'Total CTA Clicks' },
    { key: 'uniqueCtaClicks', label: 'Unique CTA Clicks' },
];

const AD_STATS_CSV_EXCLUDE_KEYS = new Set(['platform', 'discussionid']);

function stripAdStatsCsvColumns(data: unknown): unknown {
    if (!Array.isArray(data)) return data;
    return data.map(row => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
        const out: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(
            row as Record<string, unknown>,
        )) {
            if (!AD_STATS_CSV_EXCLUDE_KEYS.has(key.toLowerCase())) {
                out[key] = value;
            }
        }
        return out;
    });
}

const AdStatsModal: React.FC<Props> = ({ show, report, onHide }) => {
    const { setErrorToastShow } = useReportContext();
    const [adid, setAdid] = useState('');
    const [stats, setStats] = useState<AdStatsSummary | null>(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [showDownloadOk, setShowDownloadOk] = useState(false);

    const statsEndPoint = report?.endPoint ?? 'ADgetAdStats';
    const downloadEndPoint =
        report?.adStatsDownloadEndPoint ?? 'ADgetAdStatsDetail';

    const handleClose = () => {
        setAdid('');
        setStats(null);
        setShowDownloadOk(false);
        onHide();
    };

    const handleFetchStats = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = adid.trim();
        if (!trimmed || !report) return;

        setLoadingStats(true);
        setStats(null);
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}${statsEndPoint}`,
                { adid: trimmed },
                globalURLS.config,
            );
            if (resp?.data?.success === true && resp?.data?.data !== undefined) {
                const normalized = normalizeAdStats(resp.data.data);
                if (normalized) {
                    setStats(normalized);
                } else {
                    setErrorToastShow(true);
                }
            } else {
                setErrorToastShow(true);
            }
        } catch {
            setErrorToastShow(true);
        } finally {
            setLoadingStats(false);
        }
    };

    const handleDownload = async () => {
        const trimmed = adid.trim();
        if (!trimmed || !report) return;

        setDownloading(true);
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}${downloadEndPoint}`,
                { adid: trimmed },
                globalURLS.config,
            );
            if (resp?.data?.success === true && resp?.data?.data !== undefined) {
                const data = resp.data.data;
                const csv = dataToCsv(stripAdStatsCsvColumns(data));
                const blob = new Blob(['\uFEFF' + csv], {
                    type: 'text/csv;charset=utf-8',
                });
                const objectUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = `${downloadEndPoint}_${trimmed}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
                setShowDownloadOk(true);
            } else {
                setErrorToastShow(true);
            }
        } catch {
            setErrorToastShow(true);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <ToastContainer
                position="top-end"
                className="p-3"
                style={{ zIndex: 1080 }}
            >
                <Toast
                    bg="success"
                    show={showDownloadOk}
                    onClose={() => setShowDownloadOk(false)}
                    delay={3000}
                    autohide
                >
                    <Toast.Header closeButton>
                        <strong className="me-auto">Download complete</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        CSV saved to your downloads folder.
                    </Toast.Body>
                </Toast>
            </ToastContainer>
            <Modal
                show={show}
                onHide={handleClose}
                animation={false}
                centered
                dialogClassName="ad-stats-modal-dialog"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="mb-0">
                        {report?.name ?? 'AdStats'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleFetchStats}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="ad-stats-adid">Ad ID</Form.Label>
                            <Form.Control
                                id="ad-stats-adid"
                                type="text"
                                placeholder="Enter Ad ID"
                                value={adid}
                                onChange={e => setAdid(e.target.value)}
                                required
                                disabled={loadingStats}
                            />
                        </Form.Group>

                        {loadingStats && (
                            <div className="d-flex justify-content-center py-4">
                                <Spinner animation="border" role="status" />
                            </div>
                        )}

                        {!loadingStats && stats && (
                            <div className="ad-stats-metrics">
                                {METRIC_LABELS.map(({ key, label }) => (
                                    <div
                                        key={key}
                                        className="ad-stats-metric-card"
                                    >
                                        <div className="ad-stats-metric-label">
                                            {label}
                                        </div>
                                        <div className="ad-stats-metric-value">
                                            {stats[key].toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="ad-stats-modal-footer">
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loadingStats || !adid.trim()}
                            >
                                {loadingStats ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        Loading…
                                    </>
                                ) : (
                                    'Get Stats'
                                )}
                            </Button>
                            <Button
                                variant="success"
                                type="button"
                                disabled={
                                    downloading ||
                                    !adid.trim() ||
                                    loadingStats
                                }
                                onClick={handleDownload}
                            >
                                {downloading ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        Downloading…
                                    </>
                                ) : (
                                    'Download Full Data (CSV)'
                                )}
                            </Button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default AdStatsModal;
